import React from "react";
import { Box, SxProps, Theme } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import CustomReactMarkDown from "../../utils/markdown-wrapper.tsx";
import { i18n } from "@lingui/core";
import { t } from "@lingui/core/macro";

export interface TableContentData {
  rows: Array<{ [key: string]: any }>;
  columns: GridColDef[];
  clarification?: string;
  default_visible_columns?: string[];
}

type ColumnType = "string" | "number" | "boolean" | "date" | "dateTime";
export const MAX_DEFAULT_VISIBLE_COLUMNS = 6;

interface TableGridOptions {
  autoHeight?: boolean;
  disableColumnMenu?: boolean;
  disableRowSelectionOnClick?: boolean;
  hideFooter?: boolean;
  hideFooterSelectedRowCount?: boolean;
  showToolbar?: boolean;
  initialPageSize?: number;
  pageSizeOptions?: number[];
  sx?: SxProps<Theme>;
  columnMinWidth?: number;
  maxDefaultVisibleColumns?: number;
}

interface TableContentProps {
  content: TableContentData;
  clarificationCompact?: boolean;
  gridWrapperSx?: SxProps<Theme>;
  invalidContent?: React.ReactNode;
  gridOptions?: TableGridOptions;
}

// Shared table surface styling (row background + corner radius), used by every
// DataGrid so the conversation-panel and dashboard-tile tables cannot drift apart.
// Values taken verbatim from the dashboard tile, which is the visual reference.
export const TABLE_SURFACE_SX: SxProps<Theme> = {
  backgroundColor: "rgba(255,255,255,0.35)",
  borderRadius: 3,
  border: 0,
};

export const validateTable = (rows: any[], columns: any[]): boolean => {
  if (!rows || !columns || columns.length === 0) {
    return false;
  }

  if (
    !columns.every(
      (col) => typeof col === "object" && col !== null && "field" in col,
    )
  ) {
    return false;
  }

  const columnFields = columns.map((col) => col.field);

  return rows.every((row) => {
    if (typeof row !== "object" || row === null) {
      return false;
    }

    return columnFields.every((field) => field in row);
  });
};

export const getColumnType = (rows: any[], field: string): ColumnType => {
  const isValidDate = (d: Date) => !Number.isNaN(d.valueOf());
  const isMidnight = (d: Date) =>
    d.getHours() === 0 &&
    d.getMinutes() === 0 &&
    d.getSeconds() === 0 &&
    d.getMilliseconds() === 0;

  const isISODateOnly = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s);

  const isISODateTimeString = (s: string) =>
    /^\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+\-]\d{2}:?\d{2})?$/.test(
      s,
    );

  for (const row of rows) {
    const value = row?.[field];

    if (value == null) continue;
    if (typeof value === "string" && value.trim() === "") continue;

    if (typeof value === "boolean") return "boolean";
    if (typeof value === "number" && Number.isFinite(value)) return "number";

    if (value instanceof Date && isValidDate(value)) {
      return isMidnight(value) ? "date" : "dateTime";
    }

    if (typeof value === "string") {
      const trimmed = value.trim();

      if (isISODateTimeString(trimmed)) {
        const date = new Date(trimmed);
        if (isValidDate(date)) return "dateTime";
      }
      if (isISODateOnly(trimmed)) {
        const date = new Date(trimmed);
        if (isValidDate(date)) return "date";
      }

      return "string";
    }

    return "string";
  }

  return "string";
};

export const buildColumnVisibilityModel = (
  columns: GridColDef[],
  defaultVisibleFields: string[] | undefined,
  maxVisible: number,
): Record<string, boolean> => {
  if (columns.length <= maxVisible) {
    return {};
  }

  const availableFields = new Set(columns.map((column) => column.field));
  const visibleFields = Array.from(
    new Set(
      (defaultVisibleFields ?? []).filter((field) =>
        availableFields.has(field),
      ),
    ),
  );
  const defaultFields =
    visibleFields.length > 0
      ? visibleFields
      : columns.map((column) => column.field);
  const visibleFieldSet = new Set(defaultFields.slice(0, maxVisible));

  return Object.fromEntries(
    columns
      .filter((column) => !visibleFieldSet.has(column.field))
      .map((column) => [column.field, false]),
  );
};

const buildStyledColumns = (
  rowsWithIds: Array<{ [key: string]: any }>,
  columns: GridColDef[],
  columnMinWidth?: number,
): GridColDef[] =>
  columns.map((col) => {
    const columnType = getColumnType(rowsWithIds, col.field);

    const toDate = (value: unknown): Date | null => {
      if (value == null) return null;
      if (value instanceof Date && !Number.isNaN(value.valueOf())) return value;
      if (typeof value === "number" && Number.isFinite(value)) {
        const date = new Date(value);
        return Number.isNaN(date.valueOf()) ? null : date;
      }
      if (typeof value === "string" && value.trim() !== "") {
        const parsed = Date.parse(value);
        return Number.isNaN(parsed) ? null : new Date(parsed);
      }
      return null;
    };

    const numberFmt = new Intl.NumberFormat(i18n.locale, {
      style: "decimal",
      maximumFractionDigits: 2,
    });
    const dateFmt = new Intl.DateTimeFormat(i18n.locale, {
      dateStyle: "medium",
    });
    const dateTimeFmt = new Intl.DateTimeFormat(i18n.locale, {
      dateStyle: "medium",
      timeStyle: "short",
    });

    const valueFormatter: GridColDef["valueFormatter"] =
      columnType === "number"
        ? (value: any) => {
            if (value == null || !Number.isFinite(Number(value))) return "";
            return numberFmt.format(Number(value));
          }
        : columnType === "date"
          ? (value: any) => {
              const date = toDate(value);
              return date ? dateFmt.format(date) : "";
            }
          : columnType === "dateTime"
            ? (value: any) => {
                const date = toDate(value);
                return date ? dateTimeFmt.format(date) : "";
              }
            : columnType === "boolean"
              ? (value: any) => (value == null ? "" : String(Boolean(value)))
              : (value: any) => (value == null ? "" : String(value));

    return {
      ...col,
      headerClassName: "custom-column-header",
      flex: 1,
      minWidth: columnMinWidth,
      type: columnType as GridColDef["type"],
      valueFormatter,
    };
  });

const TableContent: React.FC<TableContentProps> = ({
  content: { rows, columns, clarification, default_visible_columns },
  clarificationCompact = false,
  gridWrapperSx,
  invalidContent,
  gridOptions,
}) => {
  const rowsWithIds = rows.map((row, index) => ({
    id: index,
    ...row,
  }));
  const styledColumns = buildStyledColumns(
    rowsWithIds,
    columns,
    gridOptions?.columnMinWidth,
  );
  const isEmptyTable = rowsWithIds.length === 0;
  const emptyTableMessage = i18n._(t`I couldn't find any data for your query.`);
  const clarificationContent = isEmptyTable
    ? clarification
      ? `${emptyTableMessage}\n\n${clarification}`
      : emptyTableMessage
    : clarification;

  return (
    <Box>
      <Box sx={gridWrapperSx}>
        {isEmptyTable ? null : validateTable(rowsWithIds, styledColumns) ? (
          <DataGrid
            autoHeight={gridOptions?.autoHeight}
            disableColumnMenu={gridOptions?.disableColumnMenu}
            disableRowSelectionOnClick={gridOptions?.disableRowSelectionOnClick}
            hideFooter={gridOptions?.hideFooter}
            hideFooterSelectedRowCount={gridOptions?.hideFooterSelectedRowCount}
            rows={rowsWithIds}
            columns={styledColumns}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: gridOptions?.initialPageSize ?? 10,
                  page: 0,
                },
              },
              ...(gridOptions?.maxDefaultVisibleColumns !== undefined
                ? {
                    columns: {
                      columnVisibilityModel: buildColumnVisibilityModel(
                        columns,
                        default_visible_columns,
                        gridOptions.maxDefaultVisibleColumns,
                      ),
                    },
                  }
                : {}),
            }}
            pageSizeOptions={gridOptions?.pageSizeOptions ?? [10, 25, 50]}
            showToolbar={gridOptions?.showToolbar}
            sx={
              [
                TABLE_SURFACE_SX,
                ...(gridOptions?.sx ? [gridOptions.sx] : []),
              ] as SxProps<Theme>
            }
          />
        ) : (
          invalidContent || (
            <Box sx={{ p: 2, color: "error.main" }}>
              Invalid table data. Please check that all required columns are
              present.
            </Box>
          )
        )}
      </Box>
      {clarificationContent && (
        <Box marginBottom={"1%"} marginTop={"2%"}>
          <CustomReactMarkDown
            content={clarificationContent}
            compact={clarificationCompact}
          />
        </Box>
      )}
    </Box>
  );
};

export default TableContent;
